import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { IoClose } from 'react-icons/io5'
import Button from '../../../elements/button'
import { useNpsInternal } from '../hooks/useNps'
import NpsQuestionRenderer from './NpsQuestion'

const NpsModal: React.FC = () => {
    const { t } = useTranslation()
    const { activeSet, responses, errors, status, setResponse, submit, close } = useNpsInternal()
    const dialogRef = useRef<HTMLDialogElement>(null)
    const previouslyFocusedRef = useRef<HTMLElement | null>(null)

    const isOpen = !!activeSet
    const isSubmitting = status === 'submitting'
    const isThankYou = status === 'thankyou'

    useEffect(() => {
        if (isOpen) {
            previouslyFocusedRef.current = document.activeElement as HTMLElement
            dialogRef.current?.focus()
        } else {
            previouslyFocusedRef.current?.focus()
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !isSubmitting) {
                close()
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, isSubmitting, close])

    if (!isOpen || !activeSet) return null

    const titleId = `nps-modal-title-${activeSet.id}`
    const descriptionId = activeSet.description ? `nps-modal-description-${activeSet.id}` : undefined

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <button
                type="button"
                aria-label={t('Close')}
                tabIndex={-1}
                className="absolute inset-0 bg-black bg-opacity-50 cursor-default"
                onClick={() => {
                    if (!isSubmitting) close()
                }}
            />
            <dialog
                ref={dialogRef}
                open
                tabIndex={-1}
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                data-testid="nps-modal"
                className="relative m-0 p-0 border-0 text-inherit bg-white rounded-[0.417rem] overflow-hidden shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col outline-none"
            >
                <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200">
                    <div className="flex flex-col gap-1">
                        <h2 id={titleId} className="text-[1rem] font-semibold text-gray-900 leading-snug">
                            {t(activeSet.title)}
                        </h2>
                        {activeSet.description && (
                            <p id={descriptionId} className="text-[0.75rem] text-gray-600">
                                {t(activeSet.description)}
                            </p>
                        )}
                    </div>
                    {!isSubmitting && (
                        <button type="button" aria-label={t('Close')} onClick={close} className="p-1 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 flex-shrink-0" data-testid="nps-modal-close">
                            <IoClose size={20} />
                        </button>
                    )}
                </div>

                {isThankYou ? (
                    <div className="p-6 flex flex-col items-center justify-center gap-3 text-center" data-testid="nps-thank-you">
                        <h3 className="text-[0.917rem] font-semibold text-gray-900">{t('Thank you for your feedback!')}</h3>
                        <div className="pt-2">
                            <Button theme="primary" size="xs" onClick={close} data-testid="nps-thank-you-close">
                                {t('Close')}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="p-4 overflow-y-auto flex flex-col gap-5">
                            {activeSet.questions.map((question) => (
                                <NpsQuestionRenderer key={question.id} question={question} value={responses[question.id]} onChange={(value) => setResponse(question.id, value)} error={errors[question.id]} />
                            ))}
                        </div>
                        <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
                            <Button theme="secondary" styleType="outline" size="xs" onClick={close} disabled={isSubmitting} data-testid="nps-cancel">
                                {t('Cancel')}
                            </Button>
                            <Button
                                theme="primary"
                                size="xs"
                                onClick={() => {
                                    void submit()
                                }}
                                disabled={isSubmitting}
                                data-testid="nps-submit"
                            >
                                {isSubmitting ? t('Submitting...') : t('Submit')}
                            </Button>
                        </div>
                    </>
                )}
            </dialog>
        </div>
    )
}

export default NpsModal
